import React, { useEffect, useState } from 'react';
import PageLayout from '../../layouts/PageLayout';
import Brochure from 'components/Brochure';
import TextPage from 'components/TextPage';
import HeroSection from 'components/HeroSection';
import ServicesNavigation from 'components/ServicesNavigation';
import NewsIcons from 'components/NewsIcons';
import ThinImagesSection from 'containers/ThinImagesSection';
import CardsCarousel from 'components/CardsCarousel';
import { getMainServicesData } from 'api-sanity/api';
import { useRouteIsLoading } from 'context/RouteIsLoadingContext';
import { useTheme } from 'hooks/useTheme';
import usePageLoadProgress from 'hooks/usePageLoadProgress';
import RangeCarousel from 'components/RangeCarousel';
import { useSanityMetaTags } from 'hooks/useSanityMetaTags';

const pID = {
  P_1: '5728ce02-3c95-4871-854c-c96311629731',
  P_2: '3f211b23-ef38-4daa-a217-10e9a36b9915',
  P_3: '273ea481-5e8d-4521-9219-ab8c5b93a4b1',
  P_4: '6fc0b712-48ee-4eb4-aceb-7a5ed5e9e360',
};

const mainServicesIds = {
  heroId: '8ec14f27-980f-42fd-b4b9-19fca6e0139a',
  ourRangeId: 'a10d9be4-bbba-4094-985d-bac29aa1978e',
  brochureId: '705fa18e-fd9e-4389-b0bc-0a755cb811b3',
  servicesSectionId: 'a0372f45-2f2d-4b76-a989-0fdc57edd143',
  newsIconsId: 'bd4f8c2e-a572-42c3-be3d-7457def94469',
  thinImagesId: 'cb79e894-8c47-45d8-afef-dbaade6d940b',
  cardsCarouselId: 'd805601c-5046-4d68-b9a5-61df941470dc',
};

const MainServices = () => {
  const [mainServicesData, setMainServicesData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { setIsLoading: setRouteContextIsLoading } = useRouteIsLoading();
  const { isDarkMode } = useTheme();

  // Fetch and apply meta tags from Sanity
  useSanityMetaTags('services');

  const { loadingProgress, isDataReady, apiDataResults } = usePageLoadProgress(
    (onProgress) => getMainServicesData(mainServicesIds, onProgress)
  );

  useEffect(() => {
    if (isDataReady && apiDataResults) {
      const [data] = apiDataResults;
      setMainServicesData(data);
      setIsLoading(false);
    }
  }, [isDataReady, apiDataResults]);

  useEffect(() => {
    setRouteContextIsLoading(isLoading);
  }, [isLoading, setRouteContextIsLoading]);

  const getParagraphById = (id) => {
    if (!mainServicesData?.textPages) return null;
    return mainServicesData.textPages.find((p) => p._id === id);
  };

  return (
    <PageLayout className='main-services-page'>
      <HeroSection
        imageUrl={
          isDarkMode
            ? mainServicesData?.heroSection?.darkModeImageUrl ||
              mainServicesData?.heroSection?.lightModeImageUrl
            : mainServicesData?.heroSection?.lightModeImageUrl
        }
        title={mainServicesData?.heroSection?.title}
        titleHighlight={mainServicesData?.heroSection?.titleHighlight}
        subtitle={mainServicesData?.heroSection?.subtitle}
        description={mainServicesData?.heroSection?.description}
        buttonText={mainServicesData?.heroSection?.buttonText}
        buttonLink={mainServicesData?.heroSection?.buttonLink}
        showThemeToggle={mainServicesData?.heroSection?.showThemeToggle}
        loaderProgress={loadingProgress}
        isLoading={isLoading}
      />
      {!isLoading && getParagraphById(pID.P_1) && (
        <TextPage
          key={getParagraphById(pID.P_1)?._id}
          type={getParagraphById(pID.P_1)?.type}
          titleActive={getParagraphById(pID.P_1)?.titleActive}
          titleInactive={getParagraphById(pID.P_1)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_1)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_1)?.paragraphText}
          buttonText={getParagraphById(pID.P_1)?.buttonText}
          buttonLink={getParagraphById(pID.P_1)?.buttonLink}
        />
      )}
      {!isLoading && (
        <ServicesNavigation data={mainServicesData?.servicesSection} />
      )}
      {!isLoading && getParagraphById(pID.P_3) && (
        <TextPage
          key={getParagraphById(pID.P_3)?._id}
          type={getParagraphById(pID.P_3)?.type}
          titleActive={getParagraphById(pID.P_3)?.titleActive}
          titleInactive={getParagraphById(pID.P_3)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_3)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_3)?.paragraphText}
          buttonText={getParagraphById(pID.P_3)?.buttonText}
          buttonLink={getParagraphById(pID.P_3)?.buttonLink}
        />
      )}
      {!isLoading && mainServicesData?.newsIcons?.items && (
        <NewsIcons items={mainServicesData.newsIcons.items} />
      )}
      {!isLoading && mainServicesData?.ourRangeData?.items && (
        <RangeCarousel rangeCarouselData={mainServicesData.ourRangeData} />
      )}
      {!isLoading && mainServicesData?.thinImages?.data && (
        <ThinImagesSection
          cards={mainServicesData.thinImages.data}
          titleGrotesk={mainServicesData.thinImages.groteskTitle}
          titleItalic={mainServicesData.thinImages.italicTitle}
          grid={mainServicesData.thinImages.grid}
        />
      )}
      {!isLoading && getParagraphById(pID.P_2) && (
        <TextPage
          key={getParagraphById(pID.P_2)?._id}
          type={getParagraphById(pID.P_2)?.type}
          titleActive={getParagraphById(pID.P_2)?.titleActive}
          titleInactive={getParagraphById(pID.P_2)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_2)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_2)?.paragraphText}
          buttonText={getParagraphById(pID.P_2)?.buttonText}
          buttonLink={getParagraphById(pID.P_2)?.buttonLink}
        />
      )}
      {!isLoading && mainServicesData?.cardsCarousel?.images && (
        <CardsCarousel cards={mainServicesData.cardsCarousel.images} />
      )}
      {!isLoading && getParagraphById(pID.P_4) && (
        <TextPage
          key={getParagraphById(pID.P_4)?._id}
          type={getParagraphById(pID.P_4)?.type}
          titleActive={getParagraphById(pID.P_4)?.titleActive}
          titleInactive={getParagraphById(pID.P_4)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_4)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_4)?.paragraphText}
          buttonText={getParagraphById(pID.P_4)?.buttonText}
          buttonLink={getParagraphById(pID.P_4)?.buttonLink}
        />
      )}
      {!isLoading && <Brochure data={mainServicesData?.brochureData} />}
    </PageLayout>
  );
};

export default MainServices;
